import { Modal, Button } from 'react-bootstrap'
import { useAuth } from '../../context/AuthContext'
import { useShieldFlow } from './hooks/useShieldFlow'
import StepHeader from './components/StepHeader'
import ApprovalStep from './steps/ApprovalStep'
import GasReviewStep from './steps/GasReviewStep'
import ConfirmationStep from './steps/ConfirmationStep'
import SuccessStep from './steps/SuccessStep'

const ShieldModal = ({ onClose, selectedToken, mode }) => {
    const { user } = useAuth()
    const { wallet } = user

    const {
        step,
        amount,
        useMaxAmount,
        actualTxHash,
        isShielding,
        actionText,
        shieldLoading,
        shieldError,
        shieldTxHash,
        unshieldLoading,
        unshieldError,
        unshieldTxHash,
        shieldEstimate,
        shieldEstimateLoading,
        shieldEstimateError,
        unshieldEstimate,
        unshieldEstimateLoading,
        unshieldEstimateError,
        approveLoading,
        approveError,
        showErrors,
        getAmountError,
        handleAmountChange,
        handleMaxClick,
        handleApprove,
        handleProceedToConfirm,
        handleConfirm,
        handleBack,
        resetModal
    } = useShieldFlow(selectedToken, mode, wallet)

    const handleClose = () => {
        resetModal()
        onClose()
    }

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <ApprovalStep
                        isShielding={isShielding}
                        actionText={actionText}
                        approveError={approveError}
                        selectedToken={selectedToken}
                        amount={amount}
                        useMaxAmount={useMaxAmount}
                        handleAmountChange={handleAmountChange}
                        handleMaxClick={handleMaxClick}
                        getAmountError={() => (showErrors ? getAmountError() : null)}
                    />
                )
            case 2:
                return (
                    <GasReviewStep
                        actionText={actionText}
                        selectedToken={selectedToken}
                        amount={amount}
                        isShielding={isShielding}
                        shieldEstimate={shieldEstimate}
                        shieldEstimateLoading={shieldEstimateLoading}
                        shieldEstimateError={shieldEstimateError}
                        unshieldEstimate={unshieldEstimate}
                        unshieldEstimateLoading={unshieldEstimateLoading}
                        unshieldEstimateError={unshieldEstimateError}
                    />
                )
            case 3:
                return (
                    <ConfirmationStep
                        shieldError={shieldError}
                        unshieldError={unshieldError}
                        amount={amount}
                        selectedToken={selectedToken}
                        actionText={actionText}
                        isShielding={isShielding}
                        shieldEstimate={shieldEstimate}
                        wallet={wallet}
                    />
                )
            case 4:
                return (
                    <SuccessStep
                        isShielding={isShielding}
                        actionText={actionText}
                        amount={amount}
                        selectedToken={selectedToken}
                        actualTxHash={actualTxHash}
                        shieldTxHash={shieldTxHash}
                        unshieldTxHash={unshieldTxHash}
                    />
                )
            default:
                return null
        }
    }

    const renderFooter = () => {
        switch (step) {
            case 1:
                return (
                    <>
                        <Button
                            variant="outline-secondary"
                            onClick={handleClose}
                            style={{
                                borderColor: 'var(--border-color)',
                                color: 'var(--text-primary)'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleApprove}
                            disabled={approveLoading} // Removed !isValidAmount()
                            className="d-flex align-items-center justify-content-center modal-button primary"
                        >
                            {approveLoading ? (
                                <div className="d-flex align-items-center justify-content-center">
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    {isShielding ? 'Approving...' : 'Processing...'}
                                </div>
                            ) : (
                                isShielding ? 'Approve Transaction' : 'Approve Transaction'
                            )}
                        </Button >
                    </>
                )
            case 2:
                return (
                    <>
                        <Button
                            variant="outline-secondary"
                            onClick={handleBack}
                            style={{
                                borderColor: 'var(--border-color)',
                                color: 'var(--text-primary)'
                            }}
                        >
                            Back
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleProceedToConfirm}
                            disabled={shieldEstimateLoading || unshieldEstimateLoading || shieldEstimateError || unshieldEstimateError}
                            className="d-flex align-items-center justify-content-center"
                            style={{
                                backgroundColor: 'var(--accent-color)',
                                borderColor: 'var(--accent-color)',
                                color: '#ffffff',
                                fontWeight: '500'
                            }}
                        >
                            {(shieldEstimateLoading || unshieldEstimateLoading) ? (
                                <div className="d-flex align-items-center justify-content-center">
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    Estimating...
                                </div>
                            ) : (
                                'Approve Transaction'
                            )}
                        </Button >
                    </>
                )
            case 3:
                return (
                    <>
                        <Button
                            variant="outline-secondary"
                            onClick={handleBack}
                            disabled={shieldLoading || unshieldLoading}
                            style={{
                                borderColor: 'var(--border-color)',
                                color: 'var(--text-primary)'
                            }}
                        >
                            Back
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleConfirm}
                            disabled={shieldLoading || unshieldLoading}
                            className="d-flex align-items-center justify-content-center"
                            style={{
                                backgroundColor: 'var(--accent-color)',
                                borderColor: 'var(--accent-color)',
                                color: '#ffffff',
                                fontWeight: '500'
                            }}
                        >
                            {shieldLoading || unshieldLoading ? (
                                <div className="d-flex align-items-center justify-content-center">
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    {isShielding ? 'Shielding...' : 'Unshielding...'}
                                </div>
                            ) : (
                                `Confirm ${actionText}`
                            )}
                        </Button >
                    </>
                )
            case 4:
                return (
                    <Button
                        variant="primary"
                        onClick={handleClose}
                        className="w-100"
                        style={{
                            backgroundColor: 'var(--accent-color)',
                            borderColor: 'var(--accent-color)',
                            color: '#ffffff',
                            fontWeight: '500'
                        }}
                    >
                        Done
                    </Button>
                )
            default:
                return null
        }
    }

    return (
        <Modal
            show={true}
            onHide={handleClose}
            centered
            backdrop="static"
            keyboard={false}
            className="custom--modal sheild-unsheild-modal"
        >
            <div className="modal-header m-0">
                <h2><StepHeader step={step} actionText={actionText} /></h2>
                <button className="close-button" onClick={onClose}>
                    ✕
                </button>
            </div>

            <Modal.Body style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
                {renderStep()}
            </Modal.Body>

            <Modal.Footer style={{ backgroundColor: 'var(--bg-primary)', borderTop: '1px solid var(--border-color)' }}>
                {renderFooter()}
            </Modal.Footer>
        </Modal>
    )
}

export default ShieldModal
